/**
 * useSectionCompanyOptions — opcje filtra „Firma" w tabeli sekcji kokpitu decyzji: pamiętane listy
 * firm per zakres, prośba o listę tylko w zapytaniu „bazowym" i jednorazowe ponowienie, gdy zakres
 * nie ma jeszcze własnej listy (pełny opis reguł → `DecisionSectionTable.tsx`, „Filtr «Firma»").
 *
 * Mechanizm jest w DWÓCH hakach, bo w jednym renderze dane krążą w pętli: opcje firm → kolumny →
 * serializowane filtry → prośba o listę (`wantCompanyOptions`) → zapytanie → odpowiedź → opcje
 * firm. Jednym wywołaniem tego nie da się zamknąć (opcje są potrzebne PRZED zbudowaniem kolumn,
 * a odpowiedź dopiero PO zapytaniu), więc:
 *  1. `useSectionCompanyOptions` — wołany przed budową kolumn: stan, opcje filtra i funkcja
 *     `wantCompanyOptionsFor` (prośba o listę — wołana, gdy znane są filtry nagłówka),
 *  2. `useSectionCompanyOptionsFromQuery` — wołany PO zapytaniu: zapamiętanie listy z odpowiedzi
 *     i jednorazowe ponowienie zapytania.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ColumnHeaderFilter } from '@/components/configurable-table';
import type {
  DecisionSectionCompanyOption,
  PendingDecisionScope,
} from '@/lib/processes/approvals-api';

import { mergeSelectedCompanies } from './company-options';

/** Brak firm (zanim serwer odpowie). */
const NO_COMPANY_OPTIONS: readonly DecisionSectionCompanyOption[] = [];

/**
 * Listy firm filtra „Firma" zapamiętane przez tabelę sekcji (patrz opis pliku tabeli, „Filtr
 * «Firma»"). Nigdy nie mutowane — zmiana to nowy obiekt (dopasowanie stanu w trakcie renderu).
 */
interface KnownCompanyOptions {
  /** Lista firm per zakres (`scopeId`), dla którego serwer ją przysłał. */
  byScope: ReadonlyMap<string, readonly DecisionSectionCompanyOption[]>;
  /**
   * Ostatnio zapamiętana lista z DOWOLNEGO zakresu — zastępcza, dopóki bieżący zakres nie ma
   * własnej (filtr nie znika na czas pobrania).
   */
  last: readonly DecisionSectionCompanyOption[];
}

const NO_KNOWN_COMPANY_OPTIONS: KnownCompanyOptions = {
  byScope: new Map<string, readonly DecisionSectionCompanyOption[]>(),
  last: NO_COMPANY_OPTIONS,
};

export interface UseSectionCompanyOptionsArgs {
  scope: PendingDecisionScope;
  overdue: boolean;
  /** Firma z górnego paska (brak = „Wszystkie firmy") — osobna pamięć listy firm per firma. */
  companyId?: string;
  /** Filtr „Firma" z nagłówka tabeli (`headerFilters.company`) — z niego wynikają wybrane firmy. */
  companyFilter: ColumnHeaderFilter | undefined;
  /** Bieżące sortowanie tabeli; pierwsza wartość jest „początkowym" sortem zapytania bazowego. */
  sortColumn: string | undefined;
  sortDirection: 'asc' | 'desc';
}

/**
 * Wynik fazy 1 — przekazywany też do `useSectionCompanyOptionsFromQuery`. Pola
 * `scopeCompanyOptions` i `remember` służą WYŁĄCZNIE fazie 2 (`useSectionCompanyOptionsFromQuery`),
 * wołanej w renderze TEGO SAMEGO komponentu co faza 1: `remember` zmienia stan fazy 1 w trakcie
 * renderu (dopasowanie stanu), co React dopuszcza tylko dla stanu własnego komponentu.
 */
export interface SectionCompanyOptions {
  /** Opcje filtra „Firma": firmy z serwera plus wybrane w filtrze, których serwer nie zwraca. */
  companies: readonly DecisionSectionCompanyOption[];
  /** Lista zapamiętana dla BIEŻĄCEGO zakresu; `undefined` = zakres nie ma własnej listy. */
  scopeCompanyOptions: readonly DecisionSectionCompanyOption[] | undefined;
  /**
   * Czy zapytanie ma prosić o listę firm (`companyOptions=1`): w zapytaniu „bazowym" (pierwsza
   * strona, bez filtrów nagłówka, początkowy sort) albo dopóki zakres nie ma własnej listy. Filtry
   * nagłówka (`serializedHeaderFilters`) znane są dopiero po zbudowaniu kolumn, stąd argumenty.
   * Sort pochodzi z argumentów fazy 1 (`sortColumn` / `sortDirection`), a strona i filtry z
   * argumentów wywołania — oba źródła MUSZĄ opisywać to samo zapytanie (to, które idzie do klucza
   * i funkcji pobierającej), inaczej „bazowość" zostanie oceniona dla innego zapytania.
   */
  wantCompanyOptionsFor: (offset: number, serializedHeaderFilters: string | undefined) => boolean;
  /**
   * Zapamiętuje listę z odpowiedzi (dopasowanie stanu w trakcie renderu — woła ją faza 2, w tym
   * samym renderze komponentu co faza 1; nie wołaj jej z zewnątrz).
   */
  remember: (responseCompanyOptions: readonly DecisionSectionCompanyOption[]) => void;
}

/** Faza 1 (przed budową kolumn): stan pamiętanych list firm i opcje filtra „Firma". */
export function useSectionCompanyOptions({
  scope,
  overdue,
  companyId,
  companyFilter,
  sortColumn,
  sortDirection,
}: UseSectionCompanyOptionsArgs): SectionCompanyOptions {
  const { t } = useTranslation();
  // Filtr „Firma": firmy REKORDÓW sekcji z serwera (`companyOptions=1`), a nie firmy użytkownika.
  // Listy pamiętamy OSOBNO dla każdego zakresu („Moje" / „Przeterminowane" / „Zespół"; odpowiedź
  // bez listy — np. po błędzie — niczego nie zeruje), uzupełniane PO zapytaniu (dopasowanie
  // stanu w trakcie renderu, jak `errorBaseline` w `DecisionSectionTable.tsx`). Opcje filtra
  // bierzemy z listy BIEŻĄCEGO zakresu;
  // dopóki jej nie ma, z ostatnio zapamiętanej (filtr nie znika na czas pobrania). Lista jednego
  // zakresu nie przechodzi na inny: odpowiedź z pamięci podręcznej (60 s) bywa zapytaniem bez
  // listy firm, a wracając do zakresu trzeba pokazać firmy TEGO zakresu. Firma wybrana w filtrze,
  // której serwer już nie zwraca (zeszły z niej ostatnie rekordy), zostaje na liście z zapamiętaną
  // nazwą — inaczej filtru nie dałoby się zobaczyć ani zdjąć (`mergeSelectedCompanies`).
  const scopeId = `${scope}:${overdue ? 'overdue' : 'all'}:${companyId ?? 'all-companies'}`;
  const [knownCompanies, setKnownCompanies] =
    useState<KnownCompanyOptions>(NO_KNOWN_COMPANY_OPTIONS);
  const scopeCompanyOptions = knownCompanies.byScope.get(scopeId);
  const knownCompanyOptions = scopeCompanyOptions ?? knownCompanies.last;
  // Początkowe sortowanie sekcji — wraz z pierwszą stroną bez filtrów tworzy zapytanie „bazowe".
  const [baseSort] = useState(() => ({
    column: sortColumn,
    direction: sortDirection,
  }));
  const [companyLabels, setCompanyLabels] = useState<ReadonlyMap<string, string>>(
    () => new Map<string, string>(),
  );
  const unknownCompanyLabel = t('tenant.processes.approvals.companyUnknown');
  const selectedCompanyIds = useMemo(
    () =>
      companyFilter?.mode === 'values'
        ? (companyFilter.selectedValues ?? []).filter((value): value is string => value !== null)
        : [],
    [companyFilter],
  );
  const companies = useMemo(
    () =>
      mergeSelectedCompanies(
        knownCompanyOptions,
        selectedCompanyIds,
        companyLabels,
        unknownCompanyLabel,
      ),
    [knownCompanyOptions, selectedCompanyIds, companyLabels, unknownCompanyLabel],
  );

  // Lista firm do filtra „Firma" (`companyOptions=1`) tylko w zapytaniu „bazowym" (pierwsza
  // strona, bez filtrów nagłówka, początkowy sort) albo dopóki nie znamy listy dla bieżącego
  // zakresu. Poza tym panel używa zapamiętanej listy — serwer liczy ją bez filtrów, więc zapytania
  // o kolejne strony, filtry i sorty nie muszą jej za każdym razem wyliczać. Flaga NIE wchodzi do
  // klucza zapytania (jak `wantSums`): odpowiedź bez listy nie zeruje znanej listy.
  const wantCompanyOptionsFor = (
    offset: number,
    serializedHeaderFilters: string | undefined,
  ): boolean => {
    const isBaseQuery =
      offset === 0 &&
      serializedHeaderFilters === undefined &&
      sortColumn === baseSort.column &&
      sortDirection === baseSort.direction;
    return isBaseQuery || scopeCompanyOptions === undefined;
  };

  // Firmy do filtra „Firma" z odpowiedzi, która je niosła. Nazwy firm zapamiętujemy osobno: firma
  // wybrana w filtrze może zniknąć z kolejnej odpowiedzi.
  const remember = (responseCompanyOptions: readonly DecisionSectionCompanyOption[]): void => {
    setKnownCompanies((previous) => ({
      byScope: new Map(previous.byScope).set(scopeId, responseCompanyOptions),
      last: responseCompanyOptions,
    }));
    setCompanyLabels((previous) => {
      const next = new Map(previous);
      for (const option of responseCompanyOptions) next.set(option.id, option.name);
      return next;
    });
  };

  return { companies, scopeCompanyOptions, wantCompanyOptionsFor, remember };
}

/** To, co faza 2 czyta z wyniku zapytania sekcji (`useQuery` tabeli); przekazuj cały wynik. */
export interface CompanyOptionsQuery {
  /** Odpowiedź z adnotacją, czy poproszono o listę firm (`requestedCompanyOptions`). */
  data:
    | {
        requestedCompanyOptions: boolean;
        companyOptions?: readonly DecisionSectionCompanyOption[];
      }
    | undefined;
  isPlaceholderData: boolean;
  isFetching: boolean;
  dataUpdatedAt: number;
  refetch: () => Promise<unknown>;
}

export interface UseSectionCompanyOptionsFromQueryArgs {
  /** Wynik fazy 1. */
  options: SectionCompanyOptions;
  query: CompanyOptionsQuery;
  /** Czy sekcja ma zaznaczone wiersze (zaznaczenie wstrzymuje ponowienia — jak przy sumach). */
  hasMarkedRows: boolean;
  /**
   * Czy sekcja w ogóle ma filtr „Firma" (domyślnie tak). Sekcja bez niego (kartoteka własna — jej
   * lista nie ma kolumny „Firma") nie prosi o listę firm, więc brak listy w odpowiedzi nie jest
   * powodem do ponowienia zapytania.
   */
  enabled?: boolean;
}

/**
 * Faza 2 (po zapytaniu): zapamiętanie listy firm z odpowiedzi (w trakcie renderu) i jednorazowe
 * ponowienie zapytania, gdy odpowiedź z pamięci podręcznej listy nie niosła.
 */
export function useSectionCompanyOptionsFromQuery({
  options,
  query,
  hasMarkedRows,
  enabled = true,
}: UseSectionCompanyOptionsFromQueryArgs): void {
  const { scopeCompanyOptions, remember } = options;

  // Firmy do filtra „Firma" z odpowiedzi, która je niosła (dopasowanie stanu w trakcie renderu,
  // jak `errorBaseline` w `DecisionSectionTable.tsx` — bez klatki bez kontrolki filtra po
  // pierwszym wczytaniu). Dane zastępcze
  // (poprzedni klucz zapytania) nie liczą się: mogą pochodzić z innego zakresu, a lista firm jest
  // zapamiętywana wraz z zakresem, dla którego ją wczytano (`knownCompanies.byScope`).
  const responseCompanyOptions = query.data?.companyOptions;
  if (
    responseCompanyOptions !== undefined &&
    !query.isPlaceholderData &&
    responseCompanyOptions !== scopeCompanyOptions
  ) {
    remember(responseCompanyOptions);
  }

  // Odpowiedź z pamięci podręcznej (zapytanie z filtrem lub sortem, wczytane, gdy lista firm była
  // już znana) nie niesie firm, a bieżący zakres nie ma jeszcze własnej listy: bez ponowienia
  // filtr „Firma" pokazywałby firmy innego zakresu. Jedno ponowienie tego samego klucza dociąga
  // listę (wtedy `wantCompanyOptions` jest prawdą). Raz na zestaw danych (`dataUpdatedAt`) — jak
  // sumy: serwer, który listy nie zwraca mimo prośby, ani nieudane ponowienie nie zapętlają
  // zapytania (po ponowieniu adnotacja `requestedCompanyOptions` jest prawdziwa). Przy
  // zaznaczonych wierszach nie ponawiamy (jak przy sumach).
  const { refetch: refetchSection } = query;
  const needsCompanyOptionsRefetch =
    enabled &&
    scopeCompanyOptions === undefined &&
    query.data !== undefined &&
    !query.data.requestedCompanyOptions &&
    !query.isPlaceholderData &&
    !query.isFetching;
  const companyOptionsRefetchedForRef = useRef<number | null>(null);
  const sectionDataUpdatedAt = query.dataUpdatedAt;
  useEffect(() => {
    if (hasMarkedRows || !needsCompanyOptionsRefetch) return;
    if (companyOptionsRefetchedForRef.current === sectionDataUpdatedAt) return;
    companyOptionsRefetchedForRef.current = sectionDataUpdatedAt;
    void refetchSection();
  }, [needsCompanyOptionsRefetch, hasMarkedRows, sectionDataUpdatedAt, refetchSection]);
}
